import { List } from 'immutable';

import { getLogger } from '../../utils/core';
import { accountsTypes, accountsUtils } from '../accounts';
import type { Account, AccountSubType, AccountType } from '../accounts/accountsTypes';
import { getOptionalId } from '../core/resourceStoreTypes';
import type { AccountDiscoveryData, DiscoveredAccount } from './accountDiscoveryTypes';
import { mkAccountData } from './accountDiscoveryTypes';

const log = getLogger('data/AccountDiscovery/buildUpsertableAccountData.js');

type ScoreBucket = {
  score: number;
  linkableAccountPairs: LinkableAccountPair[];
};

type LinkableAccountPair = {
  discoveredAccount: DiscoveredAccount;
  account: Account;
};

type DraftAccountData = {
  discoveredAccount: DiscoveredAccount;
  linkToAccountId: string | null | undefined;
  linkableAccountIdsSection1: List<string | null | undefined>;
  linkableAccountIdsSection2: List<string | null | undefined>;
  linkableAccountIdsSection3: List<string | null | undefined>;
  formFieldName: string;
};

const getSortableAccountName = (
  linkableAccountsById: Map<string | null | undefined, Account>,
  accountId: string | null | undefined,
): string => linkableAccountsById.get(accountId)?.name ?? '';

const compareLinkableAccountNames = (
  linkableAccountsById: Map<string | null | undefined, Account>,
  leftAccountId: string | null | undefined,
  rightAccountId: string | null | undefined,
): number =>
  getSortableAccountName(linkableAccountsById, leftAccountId).localeCompare(
    getSortableAccountName(linkableAccountsById, rightAccountId),
    undefined,
    {
      numeric: true,
      sensitivity: 'base',
    },
  );

function isBlank(str: string | null | undefined): boolean {
  return !str || /^\s*$/.test(str);
}

export function getAccountMaskedNumber(account: Account): string | null | undefined {
  const aggregator = account.aggregators && account.aggregators.size > 0 ? account.aggregators.get(0) : null;
  return aggregator ? aggregator.accountNumberMasked : account.accountNumberMasked;
}

export function isSameMaskedNumber(
  discoveredAccount: DiscoveredAccount,
  accountNumberMasked: string | null | undefined,
) {
  return (
    !isBlank(discoveredAccount.maskedNumber) &&
    !isBlank(accountNumberMasked) &&
    discoveredAccount.maskedNumber!.replace(/^\D+/g, '') === accountNumberMasked!.replace(/^\D+/g, '')
  );
}

export function isMaskedNumberMatch(discoveredAccount: DiscoveredAccount, account: Account): boolean {
  return (
    Boolean(discoveredAccount.type) &&
    discoveredAccount.type === account.type &&
    isSameMaskedNumber(discoveredAccount, getAccountMaskedNumber(account))
  );
}

// ================================================================================================
// calculateScore
//
// Determines a score based upon likelyhood that a discoveredAccount matches an existing account.
// The higher the score the likelyhood accounts match.
// ================================================================================================

export function calculateScore(discoveredAccount: DiscoveredAccount, account: Account): number {
  const aggregator = account.aggregators && account.aggregators.size > 0 ? account.aggregators.get(0) : null;
  const accountName = aggregator ? aggregator.accountName : account.name;

  let score = 0;
  if (isSameMaskedNumber(discoveredAccount, getAccountMaskedNumber(account))) {
    log.debug('Scoring - Masked number is same, so increasing score');
    score += 50;
  }
  if (!isBlank(discoveredAccount.name) && !isBlank(accountName) && discoveredAccount.name === accountName) {
    log.debug('Scoring - Account name is same, so increasing score');
    score += 40;
  }
  if (discoveredAccount.type !== 'UNKNOWN' && discoveredAccount.type === account.type) {
    if (discoveredAccount.subType && discoveredAccount.subType === account.subType) {
      log.debug('Scoring - Type and subtype are the same, so increasing score');
      score += 10;
    } else {
      log.debug('Scoring - Type is the same, so increasing score');
      score += 5;
    }
  }
  if (discoveredAccount.type === 'UNKNOWN') {
    log.debug('Scoring - Unknown account, so increasing score');
    score += 1;
  }

  log.debug('Scoring - Total score for linkable pair', score, discoveredAccount, account);
  return score;
}

function buildUpsertableAccountData(
  accountDiscoveryData: AccountDiscoveryData | null | undefined,
  accountsBeingMigrated: List<Account> | null | undefined,
  manualLinkableAccounts: List<Account> | null | undefined,
  businessEnabled: boolean,
  fiMigrationLinkableAccounts?: List<Account> | null,
) {
  const accounts: DraftAccountData[] = [];
  const scoredLinkableAccounts: ScoreBucket[] = [];
  const institutionLoginsScores: Record<string, Record<string, number>> = {}; // { institutionLoginId: { discoveredAccountId1: score } }
  const newAccounts = accountDiscoveryData?.newAccounts;
  const deadAccounts = accountDiscoveryData?.deadAccounts;
  const unconnectedAccounts = accountDiscoveryData?.unconnectedAccounts;

  if (accountDiscoveryData && !newAccounts) {
    throw new Error('Missing newAccounts in account discovery data');
  }

  newAccounts?.forEach((discoveredAccount, index) => {
    const accountType = accountsTypes.getAccountTypeNodeByQcsType(
      discoveredAccount.type as AccountType,
      discoveredAccount.subType as AccountSubType | undefined | null,
    );

    // hide new account if not supported
    if (!accountsUtils.isAccountNodeSelectable(accountType, undefined, businessEnabled)) {
      return;
    }

    const linkableAccountsById = new Map<string | null | undefined, Account>();

    const linkableAccountIdsSection1 = new Set<string | null | undefined>();
    // Dead, unconnected and being-migrated lists can overlap; score each account once so a duplicate pair is not
    // mistaken for an ambiguous match.
    [deadAccounts, unconnectedAccounts, accountsBeingMigrated].forEach((accountList) => {
      accountList?.forEach((account) => {
        const accountId = getOptionalId(account);
        if (account.isClosed || linkableAccountsById.has(accountId)) return;
        linkableAccountsById.set(accountId, account);
        linkableAccountIdsSection1.add(accountId);
        addLinkableAccountsToScoredList(discoveredAccount, account, scoredLinkableAccounts);
      });
    });

    const linkableAccountIdsSection2: Array<string | null | undefined> = [];
    if (manualLinkableAccounts && manualLinkableAccounts.size) {
      manualLinkableAccounts.forEach((account) => {
        if (!account.isClosed) {
          linkableAccountsById.set(getOptionalId(account), account);
          linkableAccountIdsSection2.push(getOptionalId(account));
          const institutionLoginId = account.institutionLoginId || '0';
          const accountScore = calculateScore(discoveredAccount, account);
          const institutionLoginScore = institutionLoginsScores[institutionLoginId] || {};
          const discoveredCpId = String(discoveredAccount.cpId);
          institutionLoginScore[discoveredCpId] = (institutionLoginScore[discoveredCpId] || 0) + accountScore;
          institutionLoginsScores[institutionLoginId] = institutionLoginScore;
        }
      });
    }
    linkableAccountIdsSection2.sort((a, b) => compareLinkableAccountNames(linkableAccountsById, a, b));

    const linkableAccountIdsSection3: Array<string | null | undefined> = [];
    if (fiMigrationLinkableAccounts?.size) {
      fiMigrationLinkableAccounts.forEach((account) => {
        if (
          !account.isClosed &&
          !linkableAccountsById.has(getOptionalId(account)) &&
          isMaskedNumberMatch(discoveredAccount, account)
        ) {
          linkableAccountsById.set(getOptionalId(account), account);
          linkableAccountIdsSection3.push(getOptionalId(account));
          addLinkableAccountsToScoredList(discoveredAccount, account, scoredLinkableAccounts);
        }
      });
    }
    linkableAccountIdsSection3.sort((a, b) => compareLinkableAccountNames(linkableAccountsById, a, b));

    accounts.push({
      discoveredAccount,

      linkToAccountId: '0',

      linkableAccountIdsSection1: List(
        [...linkableAccountIdsSection1].sort((a, b) => compareLinkableAccountNames(linkableAccountsById, a, b)),
      ),
      linkableAccountIdsSection2: List(linkableAccountIdsSection2),
      linkableAccountIdsSection3: List(linkableAccountIdsSection3),

      formFieldName: `account_${index}`,
    });
  });

  const { institutionLoginId, accountsMatchedCount } = Object.entries(institutionLoginsScores).reduce(
    (result, [institutionLoginId, institutionLoginScores]) => {
      const accountsMatchedCount = Object.values(institutionLoginScores).reduce(
        (accountsMatched, accountScore) => (accountScore >= 50 ? accountsMatched + 1 : accountsMatched),
        0,
      );
      return accountsMatchedCount > result.accountsMatchedCount ? { institutionLoginId, accountsMatchedCount } : result;
    },
    { institutionLoginId: null as string | null, accountsMatchedCount: 0 },
  );
  if (institutionLoginId && accountsMatchedCount && manualLinkableAccounts && newAccounts && newAccounts.size) {
    manualLinkableAccounts
      .filter((account) => (account.institutionLoginId || '0') === institutionLoginId && !account.isClosed)
      .forEach((account) =>
        newAccounts.forEach((discoveredAccount) =>
          addLinkableAccountsToScoredList(discoveredAccount, account, scoredLinkableAccounts),
        ),
      );
  }

  updateAccountsWithAnyDefaultLinkActions(scoredLinkableAccounts, accounts);

  return List(accounts.map((account) => mkAccountData(account)));
}

function addLinkableAccountsToScoredList(
  discoveredAccount: DiscoveredAccount,
  account: Account,
  scoredLinkableAccounts: ScoreBucket[],
): void {
  const score = calculateScore(discoveredAccount, account);

  let linkableAccountsWithScore = scoredLinkableAccounts.find((e) => e.score === score);
  if (!linkableAccountsWithScore) {
    scoredLinkableAccounts.push((linkableAccountsWithScore = { score, linkableAccountPairs: [] }));
  }
  linkableAccountsWithScore.linkableAccountPairs.push({ discoveredAccount, account });
}

function updateAccountsWithAnyDefaultLinkActions(scoredLinkableAccounts: ScoreBucket[], accounts: DraftAccountData[]) {
  // sort scored linkable accounts in descending order by score
  scoredLinkableAccounts.sort((a, b) => b.score - a.score);

  const assignedOrAmbiguousDiscoveredAccounts: DiscoveredAccount[] = [];
  const assignedOrAmbiguousAccounts: Account[] = [];

  // for each list of linkable-accounts (ordered by highest to lowest score)
  scoredLinkableAccounts.forEach((linkableAccountsWithScore) => {
    const linkableAccounts = linkableAccountsWithScore.linkableAccountPairs;
    log.info('Processing linkable accounts with score', linkableAccountsWithScore.score, linkableAccounts);

    // for all linkable-accounts with a given score
    while (linkableAccounts.length > 0) {
      const linkableAccount = linkableAccounts.pop();
      if (!linkableAccount) {
        continue;
      }

      log.info('Processing linkable account', linkableAccount);

      const isDiscoveredAccountAlreadyAssignedOrAmbigoous =
        assignedOrAmbiguousDiscoveredAccounts.find(
          (discoveredAccount) => discoveredAccount === linkableAccount.discoveredAccount,
        ) !== undefined;
      const isAccountAlreadyAssignedOrAmbigoous =
        assignedOrAmbiguousAccounts.find((account) => account === linkableAccount.account) !== undefined;

      log.info(
        'Is already ambiguous',
        isDiscoveredAccountAlreadyAssignedOrAmbigoous,
        isAccountAlreadyAssignedOrAmbigoous,
      );

      if (!isDiscoveredAccountAlreadyAssignedOrAmbigoous && !isAccountAlreadyAssignedOrAmbigoous) {
        // is current linkableAccounts ambiguous with any other pair with the same score
        const ambiguous =
          linkableAccounts.find(
            (otherLinkableAccount) =>
              linkableAccount.discoveredAccount === otherLinkableAccount.discoveredAccount ||
              linkableAccount.account === otherLinkableAccount.account,
          ) !== undefined;

        log.info('Is ambiguous with another pair in same list', ambiguous);

        if (!ambiguous) {
          const accountData = accounts.find(
            (account) => account.discoveredAccount === linkableAccount.discoveredAccount,
          );
          log.info('Find discovered account in accounts', accountData);
          if (accountData) {
            accountData.linkToAccountId = getOptionalId(linkableAccount.account);
          }
        }
      }

      // if not already added, add linkableAccounts to isNew and isExisting assigned or ambiguous lists
      if (!isDiscoveredAccountAlreadyAssignedOrAmbigoous) {
        assignedOrAmbiguousDiscoveredAccounts.push(linkableAccount.discoveredAccount);
      }
      if (!isAccountAlreadyAssignedOrAmbigoous) {
        assignedOrAmbiguousAccounts.push(linkableAccount.account);
      }
    }
  });
}

export default buildUpsertableAccountData;
